<script setup lang="ts">
import type { ProductAlgolia } from "utils/mapProducts"
import type { CategoryProps } from "./Category.props"
import type { ListingMostSoldProps } from "components/ProductTile/Overall/ListingMostSold.props"
import { checkEnvSite } from "~/server/utils/getAlgoliaConfig"
import { buildExcludeFilter } from "~/utils/algolia"

const props = defineProps<CategoryProps>()

const route = useRoute()
const { setPageInfo } = usePageInfo()
const haveSubCategories = ref(false)
const aisComponent = ref(null)
const mostSoldComponent = ref(null)
const isAisComponentRendered = ref(false)
const isMostSoldComponentRendered = ref(false)

watch(
  () => mostSoldComponent.value,
  (newValue) => {
    isMostSoldComponentRendered.value = !!newValue
  },
  { immediate: true }
)

setPageInfo({
  ...props.pageInfo,
  query: "",
  loadPage: props.loadPage,
  listingType: "category",
  filterString: `categories.lvl${props.breadcrumbs?.links.length}.slug:${props.pageInfo?.slug}`,
  pageTitle: "Tutti i prodotti"
})

useState(
  `breadcrumbs-length-${route.path}`,
  () => props.breadcrumbs?.links.length
)

const { bannersList } = await useFetchBannerLists(
  `categories.lvl${props.breadcrumbs?.links.length}.slug:${props.pageInfo?.slug}`
)

const { data: footerListSubCategories } = await useAsyncData(
  "getSubCategories",
  async () => $fetch(`/api/plp/getFooterCategories/${props.pageInfo.slug}`)
)

const serverRendered = useState(`server-rendered-${route.path}`, () => false)
onServerPrefetch(() => {
  serverRendered.value = true
})

watch(
  () => aisComponent.value,
  (newValue) => {
    isAisComponentRendered.value = !!newValue
  },
  { immediate: true }
)

const isChildOfScentsCategory = computed(() => {
  const page = props.pageInfo
  const keyword = "profumi-e-colonie"
  return (
    page.slug?.includes(keyword) ||
    page.parentCategory?.slug?.includes(keyword) ||
    page.parentCategory?.parentCategory?.slug?.includes(keyword) ||
    page.parentCategory?.parentCategory?.parentCategory?.slug?.includes(
      keyword
    ) ||
    page.parentCategory?.parentCategory?.parentCategory?.parentCategory?.slug?.includes(
      keyword
    ) ||
    page.parentCategory?.parentCategory?.parentCategory?.parentCategory?.parentCategory?.slug?.includes(
      keyword
    ) ||
    false
  )
})

const { data: popularScentsBrands } = await useFetch(
  "/api/plp/getPopularBrands/3JkNl13torL8Kg4jSiOkd5"
)
const brandsMapping = computed(() => {
  if (popularScentsBrands.value && isChildOfScentsCategory.value) {
    return {
      title: popularScentsBrands.value.title,
      bgImageLarge: getImageData({
        altText: "Cover image",
        url: "/i5oh5hzamkwo/490XRu6niROmjvnWNdehvq/ed0ec79d1f049f964554b828b565445b/cover-banner-orange.svg",
        width: 1024,
        height: 350
      }),
      oneRowCards: popularScentsBrands.value.tabs[0].brands.map((brand) => ({
        image: brand.brandLogo,
        path: brand.slug ? `/${brand.slug}` : ""
      }))
    }
  }
})

const breadcrumbs = computed(() => {
  return {
    ...props.breadcrumbs,
    hideFirstItemInMobile: true,
    links: [{ label: "Home", to: "/" }, ...props.breadcrumbs?.links]
  }
})

const { data: result, pending: pendingMostSold } = await useLazyFetch(
  `/api/algolia/getRecommendations/recommend-1/${props.breadcrumbs?.links?.length}/${route.params.category}`,
  {
    server: false
  }
)

const suggestionProducts = computed(() => {
  const products: ProductAlgolia[] = _flattenDeep(
    result.value?.results?.map((item) => item.hits) ?? []
  )
  return (mapProductsAlgolia(products) as ListingMostSoldProps[]).map(
    (p, index) => {
      return {
        ...p,
        productImage: p.productImage
          ? {
              ...p.productImage,
              preload: index < 6,
              imgAttrs: { fetchpriority: index < 6 ? "high" : "auto" }
            }
          : undefined
      }
    }
  )
})

const categoryName = computed(() => props?.pageInfo.categoryName)
const { search } = useAlgoliaSearch(`TOP_PRODUCTS${checkEnvSite}`)

const { data: searchData } = await useAsyncData("ssr-category-results", () =>
  search({
    query: "",
    requestOptions: {
      filters: `categories.lvl0.slug:${route.params.category} OR categories.lvl1.slug:${route.params.category} OR categories.lvl2.slug:${route.params.category} OR categories.lvl3.slug:${route.params.category} OR categories.lvl4.slug:${route.params.category}`,
      hitsPerPage: 0,
      clickAnalytics: true,
      ...buildExcludeFilter({ excludeOutOfMarket: true })
    }
  })
)

onMounted(() => {
  if (searchData?.value?.nbHits === 0) {
    useHead({
      meta: [{ name: "robots", content: "noindex" }]
    })
  }
})
</script>

<template>
  <div class="plp-page">
    <div
      class="container-plp"
      v-if="searchData?.nbHits && searchData?.nbHits > 0"
    >
      <div class="pages-category">
        <!-- Breadcrumbs -->
        <UtilsBreadcrumbs v-if="breadcrumbs" v-bind="breadcrumbs" />
        <AlgoliaListingPage>
          <template #content>
            <ClientOnly>
              <div v-if="haveSubCategories && brandsMapping">
                <RailsPopularBrands v-bind="brandsMapping" />
              </div>
            </ClientOnly>
            <SkeletonMostSold
              v-if="pendingMostSold && !isMostSoldComponentRendered"
              class="hidden-no-script"
            />
            <RailsProduct
              v-if="suggestionProducts.length"
              ref="mostSoldComponent"
              class="bg-grey-main"
              :suggested-cards="suggestionProducts"
              type-card="MostSold"
              :title="$t('rails.product.mostSold')"
              background-color="grey"
            />
          </template>
          <template #products="slotProps">
            <GAListPageList :name="pageInfo?.categoryName ?? ''">
              <GridListCategory
                ref="aisComponent"
                title="Tutti i prodotti"
                :products-list="slotProps.items"
                :banner-list="bannersList"
                :list-name="pageInfo?.categoryName ?? ''"
              />
            </GAListPageList>
          </template>
        </AlgoliaListingPage>

        <!-- TODO: footerbanner to make connector -->
        <!-- <div class="grid-standard my-6">
        <BannerDiscountPromoHorizontal
          class="col-span-full"
          v-bind="BannerDiscountStory.args"
        />
      </div> -->
        <TextBlocksCategory
          v-if="pageInfo.footerText"
          :description="pageInfo.footerText"
        />

        <TextBlocksCategory
          v-if="footerListSubCategories?.length"
          is-grid
          :list-categories="footerListSubCategories"
          :title="
            $t('plp.footerListSubCategories', {
              name: pageInfo?.categoryName
            })
          "
        />
      </div>
    </div>
    <div v-else>
      <div class="no-results" v-if="categoryName">
        <PagesNoResults :product="categoryName" />
      </div>
    </div>
  </div>
</template>
